import {IconButton} from '@wandb/components';
import {type StructuredPatch} from 'diff';
import React, {useState} from 'react';

import {Analytics} from '../../../services/analytics';
import {PatchDelta} from '../PatchDelta';
import {PatchName} from './PatchName';
import {StructuredPatchViewer} from './StructuredPatchViewer';
import type {RenderMode} from './types';

type PatchItemProps = {
  artifactId: string;
  patch: StructuredPatch;
  renderMode: RenderMode;
  stats: {adds: number; dels: number};
};

export function PatchItem({
  artifactId,
  patch,
  renderMode,
  stats,
}: PatchItemProps) {
  // Applying the patch and rendering the diff can be expensive, so we only do
  // it when the user explicitly expands the patch item. This triggers the
  // StructuredPatchViewer to mount, which kicks off the expensive stuff.
  const [showPatch, setShowPatch] = useState(false);

  function onToggleShowPatch() {
    setShowPatch(v => !v);
    Analytics.track('Patch file', {
      action: 'visibility changed',
      value: showPatch ? 'closed' : 'opened',
    });
  }

  return (
    <div className="flex flex-col border border-wb-border rounded-lg overflow-hidden bg-wb-surface-tint">
      <div
        className="flex flex-row items-center px-8 gap-4"
        role="button"
        onClick={onToggleShowPatch}>
        <IconButton
          aria-label="expand"
          icon={showPatch ? 'chevron-down' : 'chevron-next'}
          variant="ghost"
          onPress={onToggleShowPatch}
        />
        <div className="font-semibold flex items-center gap-8">
          <PatchName patch={patch} />
        </div>
        <div className="grow shrink" />
        <div className="flex flex-row items-center gap-4 py-8">
          <PatchDelta adds={stats.adds} dels={stats.dels} />
        </div>
      </div>
      {showPatch ? (
        <div className="border-t border-wb-border bg-wb-surface">
          <StructuredPatchViewer
            artifactId={artifactId}
            patch={patch}
            renderMode={renderMode}
          />
        </div>
      ) : null}
    </div>
  );
}
